// Renders a `TextBody` per §2.2 with the format applied. The doc lets
// every text body carry:
//   • `mentions[]`    — already pill-rendered inside `MessageBubble`'s
//                       inline body container. We accept a pre-parsed
//                       mention list here and inline the pills the
//                       same way.
//   • `format: "rich"`— spans inside `body` may be bold/italic/strike/
//                       code, plus standalone `code_blocks[]` and
//                       blockquote ranges.
//   • `spoilers[]`    — hidden until tap.
//
// The text body itself is plaintext; rich-text formatting is described
// out-of-band by ranges. That keeps the wire format compact and lets
// the receiver decide how to render. The Figma source (#8 "Format
// Options") shows the composer's toolbar — the bubble-side renderer
// here is the receiver complement.
//
// Pure presentational. The host parses the envelope into a `TextBody`
// via `parseEnvelope` upstream; this component takes the body + a
// few visual hooks and returns the rendered RN tree.

import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';
import type {
  CodeBlock,
  Mention,
  TextBody,
  TextRange,
} from '../appEvent/types';

// React Native lacks a `mono` token; this is the canonical monospace
// stack used by the composer's code-block toolbar action (Figma
// `3279:11564`). Falls back to RN's platform default monospace.
const MONO_FONT_FAMILY = 'Menlo, Monaco, Consolas, "Liberation Mono", monospace';

export interface RichTextBodyProps {
  readonly body: TextBody;
  /** Inherits from the bubble — own bubble uses white-on-indigo etc. */
  readonly textStyle?: TextStyle | undefined;
  /** Mention pill background. Inherits from bubble theming. */
  readonly mentionColor?: string | undefined;
  readonly mentionBackgroundColor?: string | undefined;
  readonly onMentionPress?: ((userId: string) => void) | undefined;
  readonly testID?: string | undefined;
}

/**
 * Range-based formatting segments. We walk the body string once,
 * splitting at every range boundary, and emit one segment per
 * resulting span with the union of formats that apply.
 */
interface Segment {
  readonly start: number;
  readonly end: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly strike: boolean;
  readonly code: boolean;
  readonly spoiler: boolean;
  readonly mention?: Mention | undefined;
}

// Markdown-style markers we recognize inside the plaintext body when
// `format === 'rich'`. The wire format prefers range-based descriptors
// (so the bubble doesn't need to escape `*` in plain text), but many
// senders today emit markdown-style markers — we accept both so old +
// new senders interoperate.
const INLINE_PATTERNS: Array<{
  readonly format: 'bold' | 'italic' | 'strike' | 'code';
  readonly regex: RegExp;
}> = [
  { format: 'code', regex: /`([^`\n]+)`/g },
  { format: 'bold', regex: /\*\*([^*\n]+)\*\*/g },
  { format: 'italic', regex: /(?<!\*)\*([^*\n]+)\*(?!\*)/g },
  { format: 'strike', regex: /~~([^~\n]+)~~/g },
];

/**
 * Inline-marker spans found via regex sweep. Output ranges are over
 * the ORIGINAL body coordinate space (with markers still present);
 * the renderer strips them by skipping the marker characters when
 * emitting segments.
 */
interface MarkerSpan {
  readonly outerStart: number;   // index of first marker char
  readonly innerStart: number;   // index after opening marker
  readonly innerEnd: number;     // index of closing marker
  readonly outerEnd: number;     // index after closing marker
  readonly format: 'bold' | 'italic' | 'strike' | 'code';
}

function findMarkerSpans(body: string): ReadonlyArray<MarkerSpan> {
  const spans: MarkerSpan[] = [];
  for (const pattern of INLINE_PATTERNS) {
    const re = new RegExp(pattern.regex.source, pattern.regex.flags);
    let match: RegExpExecArray | null;
    while ((match = re.exec(body)) !== null) {
      const outerStart = match.index;
      const outerEnd = match.index + match[0].length;
      const inner = match[1] ?? '';
      const innerStart =
        pattern.format === 'code' || pattern.format === 'italic'
          ? outerStart + 1
          : outerStart + 2;
      const innerEnd = innerStart + inner.length;
      // Skip nested matches whose outer span is contained in a
      // higher-priority pattern (e.g. bold `**code**` shouldn't get
      // italicized).
      const overlaps = spans.some(
        (s) => outerStart >= s.outerStart && outerEnd <= s.outerEnd,
      );
      if (!overlaps) {
        spans.push({
          outerStart,
          innerStart,
          innerEnd,
          outerEnd,
          format: pattern.format,
        });
      }
    }
  }
  return spans;
}

export const RichTextBody = React.memo<RichTextBodyProps>(
  ({
    body,
    textStyle,
    mentionColor,
    mentionBackgroundColor,
    onMentionPress,
    testID,
  }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' =
      scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const isRich = body.format === 'rich';
    const text = body.body;

    // Code blocks come as standalone ranges; rendered as a `<View>`
    // sibling above the inline flow rather than mixed into the
    // wrapping text.
    const codeBlocks = isRich ? body.code_blocks ?? [] : [];
    // `findMarkerSpans` compiles regexes + sweeps the text and returns a fresh
    // array; memoize it on `[isRich, text]` so it doesn't re-run every render
    // and, crucially, doesn't hand `segments` a new-identity `inlineRanges`
    // each render (which would defeat that memo too).
    const inlineRanges = useMemo(
      () => (isRich ? findMarkerSpans(text) : []),
      [isRich, text],
    );

    const segments = useMemo(
      () => buildSegments(text, inlineRanges, body.mentions ?? [], body.spoilers ?? []),
      [text, inlineRanges, body.mentions, body.spoilers],
    );

    return (
      <View accessibilityLabel="Message body" testID={testID}>
        {/*
          Inline run. We render every segment as a `<Text>` sibling
          inside the parent `Text` so RN merges them into a single
          wrappable text node with per-segment styles applied.
        */}
        <Text style={textStyle}>
          {segments.map((seg, index) => (
            <SegmentText
              key={index}
              segment={seg}
              text={text}
              baseStyle={textStyle}
              colors={colors}
              {...(mentionColor !== undefined ? { mentionColor } : {})}
              {...(mentionBackgroundColor !== undefined
                ? { mentionBackgroundColor }
                : {})}
              {...(onMentionPress !== undefined ? { onMentionPress } : {})}
            />
          ))}
        </Text>
        {/*
          Standalone code blocks — surface, monospaced, indented.
          Sit below the inline body in document flow.
        */}
        {codeBlocks.map((block, index) => (
          <View
            key={`code-${index}`}
            style={[
              styles.codeBlock,
              { backgroundColor: colors.surface.secondary, borderColor: colors.border.secondary },
            ]}
          >
            <Text style={[styles.codeBlockText, { color: colors.text.primary }]}>
              {text.slice(block.range.start, block.range.end)}
            </Text>
            {block.language !== undefined && block.language.length > 0 ? (
              <Text style={[styles.codeBlockLang, { color: colors.text.tertiary }]}>
                {block.language}
              </Text>
            ) : null}
          </View>
        ))}
      </View>
    );
  },
);

RichTextBody.displayName = 'RichTextBody';

/**
 * `tokens.color.semantic[colorScheme]` is a union of the `light` and
 * `dark` variants — structurally identical but typed independently
 * by the design-tokens package. We narrow to either side here so the
 * variant indirection at the call site stays type-safe.
 */
type ColorSet =
  | typeof tokens.color.semantic.light
  | typeof tokens.color.semantic.dark;

interface SegmentTextProps {
  readonly segment: Segment;
  readonly text: string;
  readonly baseStyle?: TextStyle | undefined;
  readonly colors: ColorSet;
  readonly mentionColor?: string | undefined;
  readonly mentionBackgroundColor?: string | undefined;
  readonly onMentionPress?: ((userId: string) => void) | undefined;
}

function SegmentText({
  segment,
  text,
  baseStyle,
  colors,
  mentionColor,
  mentionBackgroundColor,
  onMentionPress,
}: SegmentTextProps): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const slice = text.slice(segment.start, segment.end);

  if (segment.mention !== undefined) {
    const m = segment.mention;
    // Mentions render in the PRIMARY tint (`accent.brand` = #537EFF) with a
    // subtle same-hue tint background, in BOTH bubble + compact modes. The
    // bubble may override either via the `mentionColor` /
    // `mentionBackgroundColor` props (e.g. white-on-indigo own bubbles);
    // otherwise we fall back to the brand tint here. The `22` alpha suffix
    // gives a ~13% wash so the pill reads as tinted, not solid.
    const fg = mentionColor ?? colors.accent.brand;
    const bg = mentionBackgroundColor ?? `${colors.accent.brand}22`;
    return (
      <Text
        accessibilityRole="link"
        onPress={() => onMentionPress?.(m.userId)}
        style={[styles.mention, { backgroundColor: bg, color: fg }]}
      >
        @{m.displayName ?? slice.replace(/^@/, '')}
      </Text>
    );
  }

  const style: TextStyle = { ...baseStyle };
  if (segment.bold) style.fontWeight = '700';
  if (segment.italic) style.fontStyle = 'italic';
  if (segment.strike) style.textDecorationLine = 'line-through';
  if (segment.code) {
    style.fontFamily = MONO_FONT_FAMILY;
    style.backgroundColor = colors.surface.secondary;
  }

  if (segment.spoiler && !revealed) {
    return (
      <Text
        onPress={() => setRevealed(true)}
        style={[style, styles.spoilerHidden, { backgroundColor: colors.text.primary, color: colors.text.primary }]}
        accessibilityLabel="Spoiler, tap to reveal"
      >
        {slice}
      </Text>
    );
  }

  return <Text style={style}>{slice}</Text>;
}

function rangesOverlap(a: TextRange, b: TextRange): boolean {
  return a.start < b.end && b.start < a.end;
}

function buildSegments(
  text: string,
  inlineRanges: ReadonlyArray<MarkerSpan>,
  mentions: ReadonlyArray<Mention>,
  spoilers: ReadonlyArray<TextRange>,
): ReadonlyArray<Segment> {
  // Build a sorted list of boundaries in coordinate space, then sweep.
  const boundaries = new Set<number>([0, text.length]);
  for (const r of inlineRanges) {
    boundaries.add(r.outerStart);
    boundaries.add(r.innerStart);
    boundaries.add(r.innerEnd);
    boundaries.add(r.outerEnd);
  }
  for (const m of mentions) {
    boundaries.add(m.range.start);
    boundaries.add(m.range.end);
  }
  for (const s of spoilers) {
    boundaries.add(s.start);
    boundaries.add(s.end);
  }
  const sorted = Array.from(boundaries).sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i]!;
    const end = sorted[i + 1]!;
    if (end <= start) continue;
    // Drop coordinates that fall inside a marker (`*`, `**`, ``` ` ```,
    // `~~`) — we don't want to render the markers themselves.
    const insideMarker = inlineRanges.some(
      (r) =>
        (start >= r.outerStart && end <= r.innerStart) ||
        (start >= r.innerEnd && end <= r.outerEnd),
    );
    if (insideMarker) continue;

    const mention = mentions.find((m) =>
      start >= m.range.start && end <= m.range.end,
    );
    const spoiler = spoilers.some((s) => rangesOverlap({ start, end }, s));
    const bold = inlineRanges.some(
      (r) =>
        r.format === 'bold' && start >= r.innerStart && end <= r.innerEnd,
    );
    const italic = inlineRanges.some(
      (r) =>
        r.format === 'italic' && start >= r.innerStart && end <= r.innerEnd,
    );
    const strike = inlineRanges.some(
      (r) =>
        r.format === 'strike' && start >= r.innerStart && end <= r.innerEnd,
    );
    const code = inlineRanges.some(
      (r) =>
        r.format === 'code' && start >= r.innerStart && end <= r.innerEnd,
    );
    segments.push({
      start,
      end,
      bold,
      italic,
      strike,
      code,
      spoiler,
      ...(mention !== undefined ? { mention } : {}),
    });
  }
  return segments;
}

const styles = StyleSheet.create({
  mention: {
    fontWeight: '600',
    paddingHorizontal: 2,
    borderRadius: 4,
  },
  spoilerHidden: {
    // Background + foreground share the same color so the text is
    // hidden until the user taps to reveal.
    borderRadius: 4,
  },
  codeBlock: {
    marginTop: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
    borderWidth: StyleSheet.hairlineWidth,
  },
  codeBlockText: {
    fontFamily: MONO_FONT_FAMILY,
    fontSize: 13,
    lineHeight: 18,
  },
  codeBlockLang: {
    fontFamily: MONO_FONT_FAMILY,
    fontSize: 10,
    marginTop: 2,
  },
});

// Expose the segment helpers for tests / hosts that want to compute
// the segmentation without rendering. Pure functions, no React deps.
export { buildSegments as __test_buildSegments, findMarkerSpans as __test_findMarkerSpans };

// CodeBlock surfaced for callers that build their own TextBody shapes.
export type { CodeBlock };
